import { useEffect, useRef, type PropsWithChildren, type ReactNode } from 'react'
import { ScrollView, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { PlaceholderLine } from '@/src/elements/placeholder'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { GTM } from '@/src/general/utils/gtm'
import { EVENTS } from '@/src/general/utils/gtm/constants/Events.enum'
import type { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants/TrackingSource.enum'

import { Pray } from './Pray'

const getPrayerDuration = (startTime: number) => Math.ceil((Date.now() - startTime) / 1000)

export type TPrayScreenProps = {
  testID?: string
  title?: string
  trackingSource: TRACKING_SOURCE
  onPray: VoidFunction
  onAmen: VoidFunction
  onClose: VoidFunction
  isLoading?: boolean
}

export const PrayScreen = ({
  children,
  testID,
  trackingSource,
  onPray,
  onAmen,
  onClose,
  title,
  isLoading,
}: PropsWithChildren<TPrayScreenProps>): ReactNode => {
  const { current: startTime } = useRef(Date.now())

  useEffect(() => GTM.sendEvent(EVENTS.PRIERE_START, { trackingSource }), [trackingSource])

  const handlePrayed = () => {
    onPray()
    if (trackingSource) GTM.sendEvent(EVENTS.PRIERE_FINISHED, { trackingSource })
  }

  const handleClose = () => {
    GTM.sendEvent(EVENTS.PRIERE_INTERRUPTED, { trackingSource, prayerDuration: getPrayerDuration(startTime) })
    onClose()
  }

  const handleAmen = () => {
    GTM.sendEvent(EVENTS.PRIERE_AMEN_CLICKED, { trackingSource, prayerDuration: getPrayerDuration(startTime) })
    onAmen()
  }

  const renderTitle = () => {
    if (title)
      return (
        <Typography testID={TEST_ID.PRAY.SCREEN.TITLE} bold colors="primary" className="text-center" size="lg">
          {title}
        </Typography>
      )
    if (isLoading)
      return <PlaceholderLine className="max-w-96 animate-pulse self-center" testID={TEST_ID.PRAY.SCREEN.LOADING} />
    return null
  }

  return (
    <ScrollView
      testID={testID}
      className="web:h-full w-full"
      contentContainerClassName="min-h-full h-full min-h-[720px] justify-between native:pt-safe native:pb-safe"
    >
      <View className="bg-primary-bg-light px-14 py-6">
        <Button
          isIconOnly
          startIcon={ICON.CLOSE}
          variant="light"
          contrast="low"
          colors="primary"
          testID={TEST_ID.PRAY.SCREEN.CLOSE}
          className="absolute right-2 top-2"
          onPress={handleClose}
        />
        {renderTitle()}
      </View>
      {children}
      <Pray onPrayed={handlePrayed} onAmen={handleAmen} isLoading={isLoading} />
    </ScrollView>
  )
}
